import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateUserAdditionalDetails } from '../../utils/api';
import styles from './AdditionalDetailsPage.module.css';
import { useAuth } from '../../context/AuthContext';
import { useProgressBar } from '../../context/ProgressBarContext';
import MessageBox from '../../components/MessageBox';
import RegistrationSteps from '../../components/RegistrationSteps';
import CustomSelect from '../../components/CustomSelect';

function AdditionalDetailsPage() {
  const [formData, setFormData] = useState({
    gender: '',
    maritalStatus: '',
    annualIncome: '',
    tradingExperience: '',
  });
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState(null);
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { showProgressBar, updateProgressBar, hideProgressBar } = useProgressBar();

  useEffect(() => {
    if (user) {
      setFormData({
        gender: user.gender || '',
        maritalStatus: user.maritalStatus || '',
        annualIncome: user.annualIncome || '',
        tradingExperience: user.tradingExperience || '',
      });
    }
  }, [user]);

  const { gender, maritalStatus, annualIncome, tradingExperience } = formData;

  const handleChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const validate = () => {
    let tempErrors = {};
    if (!gender) tempErrors.gender = 'Gender is required.';
    if (!maritalStatus) tempErrors.maritalStatus = 'Marital Status is required.';
    if (!annualIncome) tempErrors.annualIncome = 'Annual Income is required.';
    if (!tradingExperience) tempErrors.tradingExperience = 'Trading Experience is required.';
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const navigateToNextStep = (updatedUser) => {
    if (updatedUser.bankStatus === 'not submitted') {
      navigate('/bank-details');
    } else {
      navigate('/account-status');
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(null);
    if (validate()) {
      showProgressBar();
      updateProgressBar(30);
      const additionalDetails = {
        gender,
        maritalStatus,
        annualIncome,
        tradingExperience,
      };

      try {
        await new Promise(resolve => setTimeout(resolve, 500));
        updateProgressBar(60);
        const { data: updatedUser } = await updateUserAdditionalDetails(additionalDetails);
        updateUser(updatedUser);
        updateProgressBar(90);
        await new Promise(resolve => setTimeout(resolve, 500));

        navigateToNextStep(updatedUser);
      } catch (err) {
        setErrorMessage(err.response?.data?.msg || 'An unexpected error occurred. Please try again.');
      } finally {
        hideProgressBar();
      }
    }
  };

  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' },
  ];

  const maritalStatusOptions = [
    { value: 'single', label: 'Single' },
    { value: 'married', label: 'Married' },
    { value: 'divorced', label: 'Divorced' },
    { value: 'widowed', label: 'Widowed' },
  ];

  const annualIncomeOptions = [
    { value: 'upto-2.5-lakhs', label: 'Upto 2.5 Lakhs' },
    { value: '2.5-lakhs-to-5-lakhs', label: '2.5 lakhs to 5 lakhs' },
    { value: 'above-5-lakhs', label: 'Above 5 lakhs' },
  ];

  const tradingExperienceOptions = [
    { value: 'less-than-1-year', label: 'Less than 1 year' },
    { value: '1-2-years', label: '1-2 years' },
    { value: '3-5-years', label: '3-5 years' },
    { value: 'more-than-5-years', label: 'More than 5 years' },
  ];

  return (
    <div className={styles.additionalDetailsPageWrapper}>
      {errorMessage && <MessageBox message={errorMessage} type="error" onClose={() => setErrorMessage(null)} />}

      <RegistrationSteps currentStep={3} />

      <div className={styles.detailsContainer}>
        <div className={styles.detailsFormSection}>
          <div className={styles.formHeader}>
            <h2>Additional Details</h2>
            <p>Step 3: Just a few more things</p>
          </div>

          <form onSubmit={onSubmit}>
            <div className={styles.formGroup}>
              <label>Gender</label>
              <CustomSelect
                options={genderOptions}
                selected={gender}
                onChange={(value) => handleChange('gender', value)}
                placeholder="Select Gender"
              />
              {errors.gender && <p className={styles.errorText}>{errors.gender}</p>}
            </div>

            <div className={styles.formGroup}>
              <label>Marital Status</label>
              <CustomSelect
                options={maritalStatusOptions}
                selected={maritalStatus}
                onChange={(value) => handleChange('maritalStatus', value)}
                placeholder="Select Marital Status"
              />
              {errors.maritalStatus && <p className={styles.errorText}>{errors.maritalStatus}</p>}
            </div>

            <div className={styles.formGroup}>
              <label>Annual Income</label>
              <CustomSelect
                options={annualIncomeOptions}
                selected={annualIncome}
                onChange={(value) => handleChange('annualIncome', value)}
                placeholder="Select Annual Income"
              />
              {errors.annualIncome && <p className={styles.errorText}>{errors.annualIncome}</p>}
            </div>

            <div className={styles.formGroup}>
              <label>Trading Experience</label>
              <CustomSelect
                options={tradingExperienceOptions}
                selected={tradingExperience}
                onChange={(value) => handleChange('tradingExperience', value)}
                placeholder="Select Trading Experience"
              />
              {errors.tradingExperience && <p className={styles.errorText}>{errors.tradingExperience}</p>}
            </div>

            <button type="submit" className={styles.btnSubmit}>Continue</button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AdditionalDetailsPage;